import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { X, ArrowRight, ArrowLeft, HelpCircle } from "lucide-react";

// Tur onboarding sekali tampil: highlight komponen krusial step-by-step.
// Pakai: <UsageTour tourKey="tour-customer-v1" steps={[...]} />
// Step: { selector?, title, text, to? } — tanpa selector = kartu tengah.
// Tur onboarding sekali tampil: highlight komponen krusial step-by-step.
// Pakai: <UsageTour tourKey="tour-x-v1" steps={[...]} />
// Step: { selector?, title, text, to?, toLabel?, thenKey? }
// Lanjutan lintas halaman: step dengan `to` + `thenKey` menyimpan flag;
// halaman tujuan memasang <UsageTour continueKey={sama} steps={dinamis}
// auto={false}> yang otomatis jalan dan dinamis mengikuti state halaman itu.
const CONTINUE_LS = "tour-continue";

export function popContinuation(wantKey) {
  try {
    const raw = localStorage.getItem(CONTINUE_LS);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (p && p.continueKey === wantKey) {
      localStorage.removeItem(CONTINUE_LS);
      return true;
    }
  } catch {}
  return null;
}

export default function UsageTour({ tourKey, steps, auto = true, continueKey = null }) {
  const [idx, setIdx] = useState(() => {
    try {
      if (continueKey && popContinuation(continueKey)) return 0;
      return auto && !localStorage.getItem(tourKey) ? 0 : -1;
    } catch {
      return -1;
    }
  });
  const [rect, setRect] = useState(null);

  const step = idx >= 0 ? steps[idx] : null;

  const measure = useCallback(() => {
    if (!step || !step.selector) {
      setRect(null);
      return;
    }
    const el = document.querySelector(`[data-testid="${step.selector}"]`);
    if (!el) {
      setRect(null);
      return;
    }
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    // ukur setelah scroll selesai
    setTimeout(() => {
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    }, 350);
  }, [step]);

  useEffect(() => {
    if (idx < 0) return;
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [idx, measure]);

  // Putar ulang tanpa reload halaman (tombol ? di header).
  useEffect(() => {
    const restart = () => setIdx(0);
    window.addEventListener(`replay-tour:${tourKey}`, restart);
    return () => window.removeEventListener(`replay-tour:${tourKey}`, restart);
  }, [tourKey]);

  const close = (save = true) => {
    try {
      if (save) localStorage.setItem(tourKey, "done");
    } catch {}
    setIdx(-1);
  };

  if (idx < 0) return null;

  const tipBelow = rect ? rect.top + rect.height + 160 < window.innerHeight : true;

  return (
    <div className="fixed inset-0 z-50" data-testid="usage-tour">
      {/* gelap */}
      <div className="absolute inset-0 bg-black/60" onClick={() => close()} />
      {/* lubang sorot */}
      {rect && (
        <div
          className="absolute rounded-2xl transition-all duration-300"
          style={{
            top: rect.top - 8,
            left: rect.left - 8,
            width: rect.width + 16,
            height: rect.height + 16,
            boxShadow: "0 0 0 9999px rgba(0,0,0,0.60)",
            border: "2px solid white",
          }}
        />
      )}
      {/* kartu penjelasan */}
      <div
        className="absolute left-1/2 -translate-x-1/2 w-[calc(100%-2.5rem)] max-w-sm"
        style={
          rect
            ? tipBelow
              ? { top: Math.min(rect.top + rect.height + 16, window.innerHeight - 260) }
              : { top: Math.max(rect.top - 250, 12) }
            : { top: "50%", transform: "translate(-50%, -50%)" }
        }
      >
        <div className="card p-5 shadow-2xl">
          <div className="flex items-start justify-between gap-3">
            <p className="font-extrabold text-brand-800">{step.title}</p>
            <button onClick={() => close()} aria-label="Tutup panduan" className="text-brand-300 hover:text-brand-600">
              <X className="h-5 w-5" />
            </button>
          </div>
          <p className="text-sm text-brand-600 mt-2 leading-relaxed">{step.text}</p>
          {step.to && (
            <Link
              to={step.to}
              onClick={() => {
                try {
                  if (step.thenKey) {
                    localStorage.setItem(CONTINUE_LS, JSON.stringify({ continueKey: step.thenKey }));
                  }
                } catch {}
                close();
              }}
              className="btn-primary w-full mt-4 text-sm !py-2.5"
            >
              {step.toLabel || "Lihat"} <ArrowRight className="h-4 w-4" />
            </Link>
          )}
          <div className="flex items-center justify-between mt-4">
            <div className="flex gap-1.5">
              {steps.map((_, i) => (
                <span
                  key={i}
                  className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-brand-600" : "w-1.5 bg-brand-200"}`}
                />
              ))}
            </div>
            <div className="flex gap-2">
              {idx > 0 && (
                <button onClick={() => setIdx(idx - 1)} className="btn-ghost text-sm !py-2">
                  <ArrowLeft className="h-4 w-4" /> Kembali
                </button>
              )}
              {idx < steps.length - 1 ? (
                <button onClick={() => setIdx(idx + 1)} className="btn-primary text-sm !py-2">
                  Lanjut <ArrowRight className="h-4 w-4" />
                </button>
              ) : (
                <button onClick={() => close()} className="btn-primary text-sm !py-2">
                  Selesai
                </button>
              )}
            </div>
          </div>
          <button onClick={() => close()} className="text-xs text-brand-400 hover:text-brand-600 mt-3">
            Lewati panduan
          </button>
        </div>
      </div>
    </div>
  );
}

export function TourReplayButton({ tourKey, dark = true }) {
  const replay = () => {
    try {
      localStorage.removeItem(tourKey);
    } catch {}
    window.dispatchEvent(new Event(`replay-tour:${tourKey}`));
  };
  return (
    <button
      onClick={replay}
      aria-label="Putar ulang panduan"
      title="Panduan penggunaan"
      className={
        dark
          ? "h-11 w-11 rounded-full bg-white/15 grid place-items-center text-white hover:bg-white/25 transition"
          : "h-9 w-9 rounded-full bg-brand-50 grid place-items-center text-brand-600 hover:bg-brand-100 transition"
      }
      data-testid="tour-replay-btn"
    >
      <HelpCircle className="h-5 w-5" />
    </button>
  );
}
